/**
 * NewReservationWizard — alta manual en cinco pasos.
 *
 * Un formulario largo asusta; cinco pasos cortos no. El orden es el del
 * producto: primero qué se vende (estancia y tipo de alojamiento), luego a
 * quién, luego por cuánto, luego en qué unidad física y por último confirmar.
 *
 * Nada de esto sale del navegador: la reserva se crea en el store de la demo y
 * desaparece al recargar. Las OTA no se teclean aquí — entrarán por el channel
 * manager cuando exista, y no hay ninguno contratado.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, CaretLeft, CaretRight, Check } from "@phosphor-icons/react";
import { acquireBodyScrollLock } from "@/lib/bodyScrollLock";
import { DEMO_TODAY_ISO } from "@/data/pmsDemoData";
import { addDays, nightsBetween, simulateAllocation, formatMoney } from "@/lib/pmsSelectors";
import { createReservation, demoNowISO } from "@/lib/pmsRuntimeStore";
import { WIZARD_STEPS, MANUAL_ORIGINS, ASSIGNMENT_MODES } from "@/data/reservationsV2Demo";
import { DATASET_CURRENCY } from "@/lib/reservationsV2Selectors";
import { SURFACE, StatusPill } from "@/pages/admin/pms/v2/V2Primitives";

export default function NewReservationWizard({ settings, unitLabel, onClose, onCreated }) {
  // El importe se guarda en `total_eur`: su moneda es la del dato, no una
  // preferencia de Ajustes. Cambiar la moneda de informes no lo reetiqueta.
  const currency = DATASET_CURRENCY;
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(() => ({
    origin: "direct_web",
    unit_type_id: settings.unitTypes[0]?.unit_type_id || "vistas",
    check_in: addDays(DEMO_TODAY_ISO, 5),
    check_out: addDays(DEMO_TODAY_ISO, 9),
    adults: 2,
    children: 0,
    guest_name: "",
    guest_email: "",
    guest_phone: "",
    total_eur: 0,
    assignment: "auto",
    manual_unit_id: "",
    manual_reason: "",
  }));

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    const release = acquireBodyScrollLock();
    return () => {
      document.removeEventListener("keydown", onKey);
      release();
    };
  }, [onClose]);

  const nights = nightsBetween(form.check_in, form.check_out);
  const unitType = settings.unitTypes.find((t) => t.unit_type_id === form.unit_type_id) || null;
  const suggested = nights * (unitType?.base_rate_eur || 0);

  // La reserva todavía no existe, así que no hay nada que ignorar en el reparto.
  const alloc = useMemo(() => {
    if (!(nights > 0)) return null;
    return simulateAllocation({
      pool_id: form.unit_type_id,
      check_in: form.check_in,
      check_out: form.check_out,
      accommodation_revenue_eur: Number(form.total_eur) || suggested,
    });
  }, [form.unit_type_id, form.check_in, form.check_out, form.total_eur, nights, suggested]);

  const eligible = alloc?.previews || [];
  const recommended = alloc?.recommended?.villa || null;

  const errors = {};
  if (!(nights > 0)) errors.check_out = "La salida debe ser posterior a la entrada";
  if (step >= 1 && !form.guest_name.trim()) errors.guest_name = "Hace falta un nombre";
  if (step >= 2 && !(Number(form.total_eur) > 0)) errors.total_eur = "El importe debe ser mayor que 0";
  if (step >= 3 && form.assignment === "manual" && !form.manual_unit_id) {
    errors.manual_unit_id = "Elige una unidad";
  }

  const stepValid = (index) => {
    if (index === 0) return nights > 0;
    if (index === 1) return form.guest_name.trim().length > 0;
    if (index === 2) return Number(form.total_eur) > 0;
    if (index === 3) return form.assignment !== "manual" || Boolean(form.manual_unit_id);
    return true;
  };

  const assignedUnitId = form.assignment === "auto"
    ? recommended?.villa_id || null
    : form.assignment === "manual"
      ? form.manual_unit_id
      : null;

  const create = () => {
    const revenue = Number(form.total_eur) || 0;
    const allocation = form.assignment === "later" || !assignedUnitId ? null : {
      recommended_villa_id: recommended?.villa_id || assignedUnitId,
      assigned_villa_id: assignedUnitId,
      mode: form.assignment === "manual" ? "manual_override" : "automatic",
      fairness_score: alloc?.recommended?.current_score ?? null,
      weights: { revenue: 0.7, occupancy: 0.3, current_month: 0.7, ytd: 0.3 },
      candidates: null,
      manual_override: form.assignment === "manual" ? {
        reason: form.manual_reason || "Asignación manual desde el alta",
        actor_user_id: "user_admin_001",
        actor_name: "Dolce Admin",
        at: demoNowISO(),
      } : null,
      calculated_at: demoNowISO(),
    };

    const reservation = createReservation({
      channel: form.origin,
      pool_id: form.unit_type_id,
      assigned_villa_id: assignedUnitId,
      check_in: form.check_in,
      check_out: form.check_out,
      accommodation_revenue_eur: revenue,
      booking_status: "confirmed",
      stay_status: "pre_arrival",
      payment_status: "unpaid",
      payment_method: null,
      guest: {
        name: form.guest_name.trim(),
        email: form.guest_email.trim() || null,
        phone: form.guest_phone.trim() || null,
        adults: Number(form.adults) || 1,
        children: Number(form.children) || 0,
      },
      allocation,
    });

    onCreated?.(reservation);
    onClose();
    navigate(`/pms/reservations/${reservation.reservation_id}`);
  };

  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" data-testid="reservation-wizard">
      <div className="absolute inset-0 bg-stone-900/40 backdrop-blur-[2px]" onClick={onClose} />
      <div className="absolute inset-x-0 bottom-0 top-auto sm:inset-0 sm:m-auto sm:h-fit sm:max-w-2xl max-h-[92vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl bg-white dark:bg-stone-950 border border-stone-200 dark:border-stone-800 pb-[calc(1rem_+_env(safe-area-inset-bottom,0px))] sm:pb-0">
        <div className="sticky top-0 z-10 bg-white dark:bg-stone-950 px-4 pt-4 pb-3 border-b border-stone-100 dark:border-stone-800 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="font-serif-dv text-xl text-stone-900 dark:text-stone-100">Nueva reserva</div>
            <div className="mt-1.5 flex flex-wrap gap-1" data-testid="wizard-steps">
              {WIZARD_STEPS.map((s, i) => (
                <span
                  key={s.key}
                  data-testid={`wizard-step-${s.key}`}
                  aria-current={i === step ? "step" : undefined}
                  className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${
                    i === step
                      ? "bg-stone-900 text-white dark:bg-stone-100 dark:text-stone-900"
                      : i < step
                        ? "text-stone-500 dark:text-stone-400"
                        : "text-stone-300 dark:text-stone-600"
                  }`}
                >
                  {s.label}
                </span>
              ))}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar"
            data-testid="wizard-close"
            className="w-11 h-11 -mr-2 -mt-2 flex items-center justify-center text-stone-500 shrink-0"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {step === 0 && (
            <StepStay
              form={form} set={set} settings={settings} nights={nights}
              unitLabel={unitLabel} errors={errors}
            />
          )}
          {step === 1 && <StepGuest form={form} set={set} errors={errors} />}
          {step === 2 && (
            <StepPricing
              form={form} set={set} nights={nights} suggested={suggested}
              errors={errors} currency={currency}
            />
          )}
          {step === 3 && (
            <StepAssignment
              form={form} set={set} eligible={eligible} recommended={recommended}
              unitLabel={unitLabel} errors={errors}
            />
          )}
          {step === 4 && (
            <StepConfirm
              form={form} nights={nights} unitType={unitType}
              assignedUnitId={assignedUnitId} unitLabel={unitLabel} currency={currency}
            />
          )}
        </div>

        <div className="sticky bottom-0 bg-white dark:bg-stone-950 border-t border-stone-100 dark:border-stone-800 px-4 py-3 flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={() => (step === 0 ? onClose() : setStep(step - 1))}
            data-testid="wizard-back"
            className="min-h-[44px] px-3 rounded-full text-xs text-stone-600 dark:text-stone-300 border border-stone-300 dark:border-stone-700 inline-flex items-center gap-1.5"
          >
            <CaretLeft size={12} weight="bold" />
            {step === 0 ? "Cancelar" : "Atrás"}
          </button>

          {step < WIZARD_STEPS.length - 1 ? (
            <button
              type="button"
              onClick={() => stepValid(step) && setStep(step + 1)}
              disabled={!stepValid(step)}
              data-testid="wizard-next"
              className={`min-h-[44px] px-4 rounded-full text-xs inline-flex items-center gap-1.5 bg-stone-900 text-white dark:bg-stone-100 dark:text-stone-900 ${
                stepValid(step) ? "" : "opacity-50 cursor-not-allowed"
              }`}
            >
              Siguiente
              <CaretRight size={12} weight="bold" />
            </button>
          ) : (
            <button
              type="button"
              onClick={create}
              data-testid="wizard-create"
              className="min-h-[44px] px-4 rounded-full text-xs inline-flex items-center gap-1.5 bg-stone-900 text-white dark:bg-stone-100 dark:text-stone-900"
            >
              <Check size={12} weight="bold" />
              Crear en la vista previa
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------
 * Pasos
 * ---------------------------------------------------------------------- */

function Field({ label, hint, error, children }) {
  return (
    <label className="block min-w-0">
      <span className="block text-[10px] uppercase tracking-widest text-stone-400 mb-1">{label}</span>
      {children}
      {error
        ? <span className="block text-[11px] text-amber-700 dark:text-amber-300 mt-1">{error}</span>
        : hint ? <span className="block text-[11px] text-stone-400 mt-1">{hint}</span> : null}
    </label>
  );
}

function StepStay({ form, set, settings, nights, unitLabel, errors }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="wizard-pane-stay">
      <Field label="Origen">
        <select
          value={form.origin}
          onChange={(e) => set({ origin: e.target.value })}
          data-testid="wizard-origin"
          className="dv-input w-full text-sm min-h-[44px]"
        >
          {MANUAL_ORIGINS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </Field>
      <Field label="Tipo de alojamiento" hint={`Lo que compra el huésped, no la ${unitLabel.singular.toLowerCase()} concreta`}>
        <select
          value={form.unit_type_id}
          onChange={(e) => set({ unit_type_id: e.target.value })}
          data-testid="wizard-unit-type"
          className="dv-input w-full text-sm min-h-[44px]"
        >
          {settings.unitTypes.map((t) => (
            <option key={t.unit_type_id} value={t.unit_type_id}>{t.name}</option>
          ))}
        </select>
      </Field>
      <Field label="Entrada">
        <input
          type="date" value={form.check_in}
          onChange={(e) => set({ check_in: e.target.value })}
          data-testid="wizard-check-in"
          className="dv-input w-full text-sm min-h-[44px]"
        />
      </Field>
      <Field label="Salida" error={errors.check_out} hint={nights > 0 ? `${nights} noches` : null}>
        <input
          type="date" value={form.check_out}
          onChange={(e) => set({ check_out: e.target.value })}
          data-testid="wizard-check-out"
          className="dv-input w-full text-sm min-h-[44px]"
        />
      </Field>
      <Field label="Adultos">
        <input
          type="number" min="1" value={form.adults}
          onChange={(e) => set({ adults: Number(e.target.value) })}
          className="dv-input w-full text-sm min-h-[44px]"
        />
      </Field>
      <Field label="Niños">
        <input
          type="number" min="0" value={form.children}
          onChange={(e) => set({ children: Number(e.target.value) })}
          className="dv-input w-full text-sm min-h-[44px]"
        />
      </Field>
    </div>
  );
}

function StepGuest({ form, set, errors }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="wizard-pane-guest">
      <Field label="Huésped principal" error={errors.guest_name}>
        <input
          type="text" value={form.guest_name}
          onChange={(e) => set({ guest_name: e.target.value })}
          data-testid="wizard-guest-name"
          placeholder="Nombre y apellidos"
          className="dv-input w-full text-sm min-h-[44px]"
        />
      </Field>
      <Field label="Email">
        <input
          type="email" value={form.guest_email}
          onChange={(e) => set({ guest_email: e.target.value })}
          data-testid="wizard-guest-email"
          className="dv-input w-full text-sm min-h-[44px]"
        />
      </Field>
      <Field label="Teléfono" hint="Los acompañantes se completan en el check-in online">
        <input
          type="tel" value={form.guest_phone}
          onChange={(e) => set({ guest_phone: e.target.value })}
          className="dv-input w-full text-sm min-h-[44px]"
        />
      </Field>
    </div>
  );
}

function StepPricing({ form, set, nights, suggested, errors, currency }) {
  return (
    <div className="space-y-3" data-testid="wizard-pane-pricing">
      <Field
        label="Importe del alojamiento"
        error={errors.total_eur}
        hint={`Tarifa base del tipo × ${nights} noches: ${formatMoney(suggested, currency)}. El importe se guarda CON el impuesto de alojamiento incluido, según Ajustes.`}
      >
        <input
          type="number" min="0" value={form.total_eur}
          onChange={(e) => set({ total_eur: Number(e.target.value) })}
          data-testid="wizard-total"
          className="dv-input w-full text-sm min-h-[44px]"
        />
      </Field>
      <button
        type="button"
        onClick={() => set({ total_eur: suggested })}
        data-testid="wizard-use-suggested"
        className="min-h-[44px] px-3 rounded-full text-xs border border-stone-300 dark:border-stone-700 text-stone-600 dark:text-stone-300"
      >
        Usar {formatMoney(suggested, currency)}
      </button>
    </div>
  );
}

function StepAssignment({ form, set, eligible, recommended, unitLabel, errors }) {
  return (
    <div className="space-y-3" data-testid="wizard-pane-assignment">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {ASSIGNMENT_MODES.map((m) => (
          <button
            key={m.value}
            type="button"
            onClick={() => set({ assignment: m.value })}
            aria-pressed={form.assignment === m.value}
            data-testid={`wizard-assignment-${m.value}`}
            className={`${SURFACE} text-left p-3 min-h-[44px] ${
              form.assignment === m.value ? "ring-1 ring-stone-400 dark:ring-stone-500" : ""
            }`}
          >
            <div className="text-sm text-stone-900 dark:text-stone-100">{m.label}</div>
            <div className="text-[11px] text-stone-500 dark:text-stone-400 mt-0.5">{m.hint}</div>
          </button>
        ))}
      </div>

      {form.assignment === "auto" && (
        <div className={`${SURFACE} p-3 text-sm`} data-testid="wizard-auto-result">
          {recommended ? (
            <>
              <span className="text-stone-500 dark:text-stone-400">Unidad propuesta: </span>
              <span className="text-stone-900 dark:text-stone-100">
                {recommended.villa_id} · {recommended.name}
              </span>
              <div className="text-[11px] text-stone-400 mt-1">
                {eligible.length} {unitLabel.plural.toLowerCase()} elegibles según las reglas de reparto configuradas.
              </div>
            </>
          ) : (
            <span className="text-amber-700 dark:text-amber-300">
              No hay ninguna unidad libre de ese tipo en esas fechas.
            </span>
          )}
        </div>
      )}

      {form.assignment === "manual" && (
        <div className="space-y-2">
          <Field label={`${unitLabel.singular} concreta`} error={errors.manual_unit_id}>
            <select
              value={form.manual_unit_id}
              onChange={(e) => set({ manual_unit_id: e.target.value })}
              data-testid="wizard-manual-unit"
              className="dv-input w-full text-sm min-h-[44px]"
            >
              <option value="">Elegir…</option>
              {eligible.map((p) => (
                <option key={p.villa.villa_id} value={p.villa.villa_id}>
                  {`${p.villa.villa_id} · ${p.villa.name}`}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Motivo" hint="Queda registrado junto a la reserva">
            <input
              type="text" value={form.manual_reason}
              onChange={(e) => set({ manual_reason: e.target.value })}
              placeholder="Petición del huésped, operativa…"
              className="dv-input w-full text-sm min-h-[44px]"
            />
          </Field>
          <StatusPill tone="amber" dot={false}>
            La asignación manual sustituye a la del motor de reparto en esta reserva.
          </StatusPill>
        </div>
      )}

      {form.assignment === "later" && (
        <div className={`${SURFACE} p-3 text-sm text-stone-600 dark:text-stone-300`}>
          La reserva quedará sobre el tipo de alojamiento, sin {unitLabel.singular.toLowerCase()}{" "}
          asignada. Aparecerá en el filtro «Sin asignar».
        </div>
      )}
    </div>
  );
}

function StepConfirm({ form, nights, unitType, assignedUnitId, unitLabel, currency }) {
  return (
    <dl className={`${SURFACE} p-3 text-sm space-y-1.5`} data-testid="wizard-pane-confirm">
      <Line label="Huésped" value={form.guest_name || "—"} />
      <Line label="Fechas" value={`${form.check_in} → ${form.check_out} · ${nights} noches`} />
      <Line label="Tipo" value={unitType?.name || "—"} />
      <Line
        label={unitLabel.singular}
        value={assignedUnitId || "Sin asignar"}
      />
      <Line label="Importe" value={formatMoney(Number(form.total_eur) || 0, currency)} />
      <Line label="Cobro" value="Sin pagar · se registra después" />
      <p className="text-[11px] text-stone-400 pt-2">
        Se crea solo en la vista previa: no hay backend ni channel manager, y al recargar
        desaparece.
      </p>
    </dl>
  );
}

function Line({ label, value }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-[11px] uppercase tracking-widest text-stone-400 shrink-0">{label}</dt>
      <dd className="text-stone-900 dark:text-stone-100 text-right min-w-0 truncate">{value}</dd>
    </div>
  );
}
